import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  ArrowDown,
  Award,
  ChevronDown,
  CircleAlert,
  Download,
  Globe,
  History,
  LoaderCircle,
  Play,
  Plug,
  Puzzle,
  RefreshCw,
  TriangleAlert,
  Zap,
} from 'lucide-react';
import {
  formatBytes,
  PROVIDERS,
  type BestStreamResult,
  type ProviderId,
  type NormalizedStream,
  type Resolution,
  type ResolveSession,
} from '@teaplus/shared';
import { useStreams } from '@/lib/queries';
import { api, errorMessage } from '@/lib/api';
import type { StreamTarget } from '@/lib/store';
import {
  lastSource,
  lastStream,
  streamFingerprint,
  streamSort,
  type StreamSort,
} from '@/lib/store';
import { ProgressBar, Skeleton } from '../ui/misc';
import { Button, buttonVariants } from '../ui/button';
import { EmptyState } from '../states';
import { cn } from '@/lib/cn';

const QUALITY_ORDER: (Resolution | 'Other')[] = [
  '8K',
  '4K',
  '1440p',
  '1080p',
  '720p',
  '480p',
  '360p',
  'SD',
  'Other',
];

const qualityOf = (s: NormalizedStream) => s.quality.resolution ?? 'Other';

/** The name is the only decoration a quality gets: the top ones in the accent, the low ones quiet. */
const qualityTone = (s: NormalizedStream) => {
  const q = qualityOf(s);
  if (q === '8K' || q === '4K') return 'text-pink';
  return q === '1440p' || q === '1080p' || q === '720p' ? 'text-text' : 'text-muted';
};

/** The columns of the stream table, shared by its heading and its rows. */
const TABLE_GRID =
  'grid grid-cols-[minmax(48px,0.6fr)_minmax(0,0.8fr)_72px_minmax(100px,1.2fr)] items-center gap-x-3';
/** The All tab adds which addon each stream is from, where there's room for it. */
const TABLE_GRID_ADDON =
  'sm:grid-cols-[72px_minmax(0,0.8fr)_72px_minmax(0,1fr)_minmax(124px,1fr)] sm:gap-x-5';

type Bucket = 'now' | 'wait' | 'blocked';

/** Will it start now, might it take a while, or can't it play at all. */
export function streamBucket(s: NormalizedStream): Bucket {
  if (s.availability === 'unsupported' || s.availability === 'needs-provider') return 'blocked';
  if (s.cached === false) return 'wait';
  return s.cached === true || s.availability === 'playable' ? 'now' : 'wait';
}

type Resolving =
  | {
      kind: 'stream';
      stream: NormalizedStream;
      session: ResolveSession | null;
      error: string | null;
    }
  | { kind: 'best'; error: string | null };

export const streamKey = (s: Pick<NormalizedStream, 'filename' | 'title'>) =>
  (s.filename ?? s.title).trim().toLowerCase();

export function StreamChooser({
  target,
  onReady,
  autoPickFrom,
  preferKey,
  onPicked,
  onLeave,
  forDownload,
}: {
  target: StreamTarget;
  onReady: (session: ResolveSession) => void;
  autoPickFrom?: string | null;
  preferKey?: string | null;
  onPicked?: (stream: NormalizedStream) => void;
  /** Closes whatever window this is in when a link goes to another page. */
  onLeave?: () => void;
  /** The main button downloads the stream instead of playing it. */
  forDownload?: boolean;
}) {
  const q = useStreams({
    type: target.type,
    id: target.mediaId,
    season: target.season,
    episode: target.episode,
  });
  const [sort, setSortKey] = useState<StreamSort>(streamSort.get);
  const [flipped, setFlipped] = useState(false);
  // Which addon's tab is open; null is All.
  const [tab, setTab] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [resolving, setResolving] = useState<Resolving | null>(null);
  const [blockedOpen, setBlockedOpen] = useState<boolean | null>(null);
  const cancelled = useRef(false);
  const autoTried = useRef(false);
  const root = useRef<HTMLDivElement>(null);
  // Where the keyboard goes back to when the "getting it ready" screen closes.
  const refocus = useRef<string | null>(null);
  const last = lastStream.get(target.mediaId, target.season, target.episode);

  const all = useMemo(() => q.data?.streams ?? [], [q.data]);
  const lastId = useMemo(
    () => (last ? all.find((s) => streamFingerprint(s) === last.fingerprint)?.id : undefined),
    [all, last],
  );
  const rank = useMemo(() => {
    const order = q.data?.addonOrder ?? [];
    return (id: string) => (order.includes(id) ? order.indexOf(id) : order.length);
  }, [q.data]);
  const addons = useMemo(() => {
    const found = new Map<string, { id: string; name: string; count: number }>();
    for (const s of all) {
      const a = found.get(s.addonId) ?? { id: s.addonId, name: s.addonName, count: 0 };
      found.set(s.addonId, { ...a, count: a.count + 1 });
    }
    return [...found.values()].sort((a, b) => rank(a.id) - rank(b.id) || a.id.localeCompare(b.id));
  }, [all, rank]);
  const inTab = useMemo(() => all.filter((s) => !tab || s.addonId === tab), [all, tab]);
  const blocked = useMemo(
    () => inTab.filter((s) => s.id !== lastId && streamBucket(s) === 'blocked'),
    [inTab, lastId],
  );
  const rows = useMemo(() => {
    const qualityRank = (s: NormalizedStream) => QUALITY_ORDER.indexOf(qualityOf(s));
    const ready = (s: NormalizedStream) => (streamBucket(s) === 'now' ? 0 : 1);
    const addonRank = (a: NormalizedStream, b: NormalizedStream) =>
      rank(a.addonId) - rank(b.addonId) || a.addonId.localeCompare(b.addonId);
    const compare = (a: NormalizedStream, b: NormalizedStream) => {
      if (sort === 'size') return (b.sizeBytes ?? -1) - (a.sizeBytes ?? -1) || ready(a) - ready(b);
      return qualityRank(a) - qualityRank(b) || ready(a) - ready(b) || addonRank(a, b);
    };
    return inTab
      .filter((s) => s.id !== lastId && streamBucket(s) !== 'blocked')
      .sort((a, b) => (flipped ? compare(b, a) : compare(a, b)));
  }, [inTab, lastId, sort, flipped, rank]);
  const lastUsedStream = inTab.find((s) => s.id === lastId);
  const selectable = [
    ...(lastUsedStream && streamBucket(lastUsedStream) !== 'blocked' ? [lastUsedStream] : []),
    ...rows,
  ];
  const selected = selectable.find((s) => s.id === selectedId) ?? selectable[0];

  const remember = (s: NormalizedStream) => {
    onPicked?.(s);
    lastSource.set(target.mediaId, s.addonId);
    lastStream.set(target.mediaId, target.season, target.episode, {
      addonId: s.addonId,
      fingerprint: streamFingerprint(s),
      label: s.title,
      at: Date.now(),
    });
  };

  async function choose(stream: NormalizedStream) {
    cancelled.current = false;
    setResolving({ kind: 'stream', stream, session: null, error: null });
    try {
      let s = await api<ResolveSession>('/api/streams/resolve', { body: { streamId: stream.id } });
      while (!cancelled.current && !['ready', 'unavailable', 'error'].includes(s.status)) {
        setResolving({ kind: 'stream', stream, session: s, error: null });
        await new Promise((r) => setTimeout(r, 2000));
        if (cancelled.current) return;
        s = await api<ResolveSession>(`/api/streams/sessions/${s.id}`);
      }
      if (cancelled.current) return;
      if (s.status === 'ready' && s.playback) {
        remember(stream);
        onReady(s);
      } else {
        setResolving({
          kind: 'stream',
          stream,
          session: s,
          error: s.message ?? 'This stream could not be resolved.',
        });
      }
    } catch (e) {
      if (!cancelled.current)
        setResolving({ kind: 'stream', stream, session: null, error: errorMessage(e) });
    }
  }

  async function findBest() {
    cancelled.current = false;
    setResolving({ kind: 'best', error: null });
    try {
      const r = await api<BestStreamResult>('/api/streams/best', {
        body: {
          type: target.type,
          id: target.mediaId,
          season: target.season,
          episode: target.episode,
        },
      });
      if (cancelled.current) return;
      const s = all.find((x) => x.id === r.streamId);
      if (s) remember(s);
      onReady(r);
    } catch (e) {
      if (!cancelled.current) setResolving({ kind: 'best', error: errorMessage(e) });
    }
  }

  useEffect(() => {
    if (!autoPickFrom || autoTried.current || !q.data) return;
    autoTried.current = true;
    const pick = q.data.streams.find(
      (s) => s.addonId === autoPickFrom && s.availability === 'playable',
    );
    if (pick) void choose(pick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data, autoPickFrom]);

  const preferTried = useRef<string | null>(null);
  useEffect(() => {
    if (!preferKey || preferTried.current === preferKey || !q.data || resolving) return;
    preferTried.current = preferKey;
    const pick = q.data.streams.find(
      (s) =>
        streamKey(s) === preferKey &&
        s.availability !== 'unsupported' &&
        s.availability !== 'needs-provider',
    );
    if (pick) void choose(pick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data, preferKey]);

  // Runs after the automatic picks above: with the streams already cached they start on mount, and
  // React's dev double run must not cancel them.
  useEffect(() => {
    cancelled.current = false;
    return () => void (cancelled.current = true);
  }, []);

  useEffect(() => {
    if (resolving || !refocus.current) return;
    root.current
      ?.querySelector<HTMLElement>(`[data-focus-key="${CSS.escape(refocus.current)}"]`)
      ?.focus();
    refocus.current = null;
  }, [resolving]);

  if (resolving) {
    const back = () => {
      cancelled.current = true;
      refocus.current = resolving.kind === 'stream' ? resolving.stream.id : 'best';
      setResolving(null);
    };
    const failed = resolving.error;
    const s = resolving.kind === 'stream' ? resolving.session : null;
    return (
      <div className="py-6 text-center" role="status" aria-live="polite">
        {failed ? (
          <>
            <CircleAlert
              className={cn(
                'mx-auto h-10 w-10',
                resolving.kind === 'best' ? 'text-warning' : 'text-danger',
              )}
            />
            <p className="mt-4 font-semibold">
              {resolving.kind === 'best'
                ? 'No reliable stream found automatically.'
                : 'This stream could not be played.'}
            </p>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted">{failed}</p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <Button variant="secondary" onClick={back} autoFocus>
                {resolving.kind === 'best' ? 'Show all streams' : 'Choose another stream'}
              </Button>
              {resolving.kind === 'stream' && (
                <Button variant="ghost" onClick={findBest}>
                  <Award className="h-4 w-4" /> Pick the best one for me
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
            {resolving.kind === 'best' ? (
              <Award className="mx-auto h-10 w-10 animate-pulse text-pink" />
            ) : (
              <LoaderCircle className="mx-auto h-10 w-10 animate-spin text-pink" />
            )}
            <p className="mt-4 font-semibold">
              {resolving.kind === 'best'
                ? 'Finding the best stream…'
                : s?.status === 'processing'
                  ? 'Your provider is preparing this stream'
                  : 'Getting your stream ready'}
            </p>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted">
              {resolving.kind === 'best'
                ? 'Trying the quickest ones first and skipping any that don’t play.'
                : (s?.message ?? `Contacting ${resolving.stream.addonName}…`)}
            </p>
            {s?.progress != null && s.progress < 100 && (
              <ProgressBar
                value={s.progress}
                className="mx-auto mt-5 max-w-xs"
                label="Provider progress"
              />
            )}
            <Button variant="ghost" className="mt-6" onClick={back} autoFocus>
              Cancel
            </Button>
          </>
        )}
      </div>
    );
  }

  if (q.isPending) {
    return (
      <div className="space-y-2" aria-label="Searching your addons" role="status">
        <p className="text-[15px] text-muted">Searching your addons…</p>
        <Skeleton className="h-16 rounded-2xl" />
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-20 rounded-2xl" />
        ))}
      </div>
    );
  }
  if (q.isError) {
    return (
      <EmptyState
        icon={<CircleAlert />}
        title="We couldn't search for streams."
        body={errorMessage(q.error)}
        action={
          <Button variant="secondary" onClick={() => q.refetch()}>
            <RefreshCw className="h-4 w-4" /> Try again
          </Button>
        }
      />
    );
  }

  const d = q.data;
  if (d.noAddons) {
    return (
      <EmptyState
        icon={<Puzzle />}
        title="No addons installed"
        body="Tea+ finds streams through addons. Add your own, or bring over the ones you use in other apps, to see what's available for this title."
        action={
          <Link to="/addons" onClick={onLeave} className={buttonVariants()}>
            Install an addon
          </Link>
        }
      />
    );
  }
  if (d.missingExternalId) {
    return (
      <EmptyState
        icon={<Globe />}
        title="No streamable identifier"
        body="This title has no IMDb identifier in the catalogue, so addons can't be searched for it."
      />
    );
  }

  const needsProvider = d.streams.some((s) => s.availability === 'needs-provider');
  const canPlaySomething = d.streams.some((s) => streamBucket(s) !== 'blocked');
  // Nothing else to show, so hiding these would only look like an empty list.
  const showBlocked = blockedOpen ?? rows.length === 0;
  const sortBy = (key: StreamSort) => {
    if (key === sort) return setFlipped(!flipped);
    streamSort.set(key);
    setSortKey(key);
    setFlipped(false);
  };
  const heading = (key: StreamSort, label: string, className?: string) => (
    <button
      onClick={() => sortBy(key)}
      aria-pressed={sort === key}
      className={cn(
        'flex items-center gap-1 rounded-lg transition-colors hover:text-text',
        sort === key && 'font-semibold text-text',
        className,
      )}
    >
      {label}
      {sort === key && (
        <ArrowDown
          className={cn('h-3.5 w-3.5 transition-transform', flipped && 'rotate-180')}
          aria-hidden
        />
      )}
    </button>
  );
  const tabs = [{ id: null, name: 'All', count: all.length }, ...addons];
  const moveTab = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const next = tabs[tabs.findIndex((a) => a.id === tab) + (e.key === 'ArrowRight' ? 1 : -1)];
    if (!next) return;
    e.preventDefault();
    setTab(next.id);
    e.currentTarget.querySelector<HTMLElement>(`[data-tab="${next.name}"]`)?.focus();
  };
  const showAddon = !tab && addons.length > 1;
  const details = (s: NormalizedStream, className?: string) => (
    <StreamDetails
      stream={s}
      provider={d.provider}
      onPlay={() => choose(s)}
      lastUsed={s.id === lastId}
      forDownload={forDownload}
      className={className}
    />
  );
  const row = (s: NormalizedStream) => (
    <>
      <StreamOptionCard
        stream={s}
        provider={d.provider}
        selected={selected?.id === s.id}
        onSelect={() => setSelectedId(s.id)}
        onPlay={() => choose(s)}
        lastUsed={s.id === lastId}
        showAddon={showAddon}
      />
      {/* Phones have no room beside the table, so the details open under the row. */}
      {selected?.id === s.id && details(s, 'mb-3 lg:hidden')}
    </>
  );

  return (
    <div ref={root} onKeyDown={moveWithArrows}>
      {needsProvider && !d.providerConnected && (
        <p className="mb-4 flex items-center gap-3 rounded-2xl bg-warning/10 px-4 py-3 text-sm text-text/90">
          <Plug className="h-5 w-5 shrink-0 text-warning" aria-hidden />
          <span className="flex-1">
            Some streams need a provider such as Real-Debrid, AllDebrid or TorBox.
          </span>
          <Link
            to="/settings/streaming"
            onClick={onLeave}
            className="rounded-lg px-1 py-1 font-semibold text-warning underline-offset-2 hover:underline"
          >
            Connect one
          </Link>
        </p>
      )}
      {d.addonErrors.length > 0 && (
        <p className="mb-4 flex items-start gap-2 text-sm text-muted">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
          <span>{d.addonErrors.map((e) => `${e.addonName}: ${e.message}`).join(' · ')}</span>
        </p>
      )}

      {d.streams.length === 0 ? (
        <EmptyState
          icon={<CircleAlert />}
          title="No streams available for this title."
          body={`Your ${d.addonsQueried} addon${d.addonsQueried === 1 ? '' : 's'} returned nothing for it. Try another addon or check back later.`}
          action={
            <Link
              to="/addons"
              onClick={onLeave}
              className={buttonVariants({ variant: 'secondary' })}
            >
              Manage addons
            </Link>
          }
        />
      ) : (
        <div>
          <div className="mb-4 flex flex-col-reverse gap-3 sm:flex-row sm:items-end sm:border-b sm:border-line">
            {addons.length > 1 ? (
              <div
                role="tablist"
                aria-label="Streams by addon"
                onKeyDown={moveTab}
                className="flex min-w-0 flex-1 gap-6 overflow-x-auto border-b border-line sm:border-b-0"
              >
                {tabs.map((a) => (
                  <button
                    key={a.name}
                    role="tab"
                    data-tab={a.name}
                    aria-selected={tab === a.id}
                    tabIndex={tab === a.id ? 0 : -1}
                    onClick={() => setTab(a.id)}
                    className={cn(
                      'shrink-0 border-b-2 pb-2.5 text-[15px] font-medium transition-colors',
                      tab === a.id
                        ? 'border-pink text-text'
                        : 'border-transparent text-muted hover:text-text',
                    )}
                  >
                    {a.name} <span className="ml-0.5 font-normal text-muted">{a.count}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex-1" />
            )}
            {canPlaySomething && (
              <Button
                variant="ghost"
                data-focus-key="best"
                onClick={findBest}
                data-tip="Tea+ tries the top streams and goes with the first that works."
                className="mb-1 shrink-0 self-end"
              >
                <Award className="h-4 w-4" aria-hidden /> Pick for me
              </Button>
            )}
          </div>

          <div className="lg:flex lg:items-start lg:gap-6">
            <div
              role={addons.length > 1 ? 'tabpanel' : undefined}
              className="min-w-0 flex-1 lg:h-[min(56dvh,460px)] lg:overflow-y-auto"
            >
              <div
                className={cn(
                  TABLE_GRID,
                  showAddon && TABLE_GRID_ADDON,
                  'sticky top-0 z-10 bg-bg-2 px-3 pb-2.5 text-[13px] font-medium text-muted',
                )}
              >
                {heading('quality', 'Quality')}
                <span>Video</span>
                {heading('size', 'Size', 'justify-self-end')}
                {showAddon && <span className="hidden sm:block">Addon</span>}
                <span>Starts</span>
              </div>
              <ul className="line-rows">
                {lastUsedStream && (
                  <li>
                    <section aria-label="Last used stream">{row(lastUsedStream)}</section>
                  </li>
                )}
                {rows.map((s) => (
                  <li key={s.id}>{row(s)}</li>
                ))}
              </ul>
              {blocked.length > 0 && (
                <section
                  aria-label="Streams that can't play right now"
                  className="mt-2 border-t border-line"
                >
                  <button
                    onClick={() => setBlockedOpen(!showBlocked)}
                    aria-expanded={showBlocked}
                    className="flex min-h-11 w-full items-center gap-2 rounded-xl px-3 text-left text-sm font-medium text-muted transition-colors hover:text-text"
                  >
                    Can&rsquo;t play right now
                    <span>{blocked.length}</span>
                    <ChevronDown
                      className={cn('h-4 w-4 transition-transform', showBlocked && 'rotate-180')}
                      aria-hidden
                    />
                  </button>
                  {showBlocked && (
                    <ul>
                      {blocked.map((s) => (
                        <li key={s.id} className="border-t border-line">
                          {row(s)}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              )}
              {all.some((s) => s.quality.estimated) && (
                <p className="border-t border-line px-3 pt-3 text-[13px] text-muted">
                  Quality details come from each addon&rsquo;s description, so they may not be
                  exact.
                </p>
              )}
            </div>
            {selected && details(selected, 'hidden w-[300px] shrink-0 lg:block')}
          </div>
        </div>
      )}
    </div>
  );
}

/** The count beside the title in the window's heading, from the search the chooser already ran. */
export function StreamCount({ target }: { target: StreamTarget }) {
  const q = useStreams({
    type: target.type,
    id: target.mediaId,
    season: target.season,
    episode: target.episode,
  });
  const count = q.data?.streams.length;
  if (!count) return null;
  return <> · {count === 1 ? '1 stream' : `${count} streams`}</>;
}

/** Up and down move between streams, on top of the usual Tab. */
function moveWithArrows(e: KeyboardEvent<HTMLDivElement>) {
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
  const rows = [...e.currentTarget.querySelectorAll<HTMLElement>('[data-stream-row]')];
  const at = rows.indexOf(document.activeElement as HTMLElement);
  if (at < 0) return;
  e.preventDefault();
  rows[at + (e.key === 'ArrowDown' ? 1 : -1)]?.focus();
}

export function StreamOptionCard({
  stream: s,
  provider,
  selected,
  onSelect,
  onPlay,
  lastUsed,
  showAddon,
}: {
  stream: NormalizedStream;
  /** The connected provider, which torrent streams play through. */
  provider?: ProviderId | null;
  selected?: boolean;
  onSelect: () => void;
  /** A double click or Enter goes straight to it, without the details' button. */
  onPlay?: () => void;
  lastUsed?: boolean;
  showAddon?: boolean;
}) {
  const blocked = streamBucket(s) === 'blocked';
  const reason =
    s.unsupportedReason ?? (s.availability === 'needs-provider' ? 'Needs a provider' : null);
  const size = formatBytes(s.sizeBytes);

  return (
    // Stays focusable when it can't play, so keyboard and screen reader users can hear why.
    <button
      data-stream-row
      data-focus-key={s.id}
      data-tip={reason ?? undefined}
      aria-pressed={blocked ? undefined : !!selected}
      aria-disabled={blocked || undefined}
      onClick={blocked ? undefined : onSelect}
      onFocus={blocked ? undefined : onSelect}
      onDoubleClick={blocked ? undefined : onPlay}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' || blocked) return;
        e.preventDefault();
        onPlay?.();
      }}
      className={cn(
        TABLE_GRID,
        showAddon && TABLE_GRID_ADDON,
        'min-h-[52px] w-full rounded-xl px-3 py-2 text-left text-[15px] font-medium transition-colors',
        selected && 'bg-pink/10',
        blocked ? 'cursor-not-allowed' : !selected && 'hover:bg-white/5',
      )}
    >
      <span className="min-w-0">
        <span className={cn('block font-bold', qualityTone(s))}>{qualityOf(s)}</span>
        {lastUsed && (
          <span className="flex items-center gap-1 text-xs whitespace-nowrap text-pink">
            <History className="h-3 w-3" aria-hidden /> Last used
          </span>
        )}
      </span>
      <span className={cn('truncate', reason ? 'text-warning' : !s.quality.codec && 'text-muted')}>
        {reason ?? s.quality.codec ?? '—'}
      </span>
      <span className="text-right whitespace-nowrap tabular-nums">
        {size && (s.sizeEstimated ? `~${size}` : size)}
      </span>
      {showAddon && <span className="hidden truncate sm:block">{s.addonName}</span>}
      <span className="flex min-w-0 items-center gap-1.5 text-sm whitespace-nowrap">
        {!blocked && <StreamStatus stream={s} provider={provider} />}
      </span>
      <span className="sr-only">{s.title}</span>
    </button>
  );
}

function StreamDetails({
  stream: s,
  provider,
  onPlay,
  lastUsed,
  forDownload,
  className,
}: {
  stream: NormalizedStream;
  provider?: ProviderId | null;
  onPlay: () => void;
  lastUsed?: boolean;
  forDownload?: boolean;
  className?: string;
}) {
  const size = formatBytes(s.sizeBytes);
  const ActionIcon = forDownload ? Download : Play;
  const facts: [string, ReactNode][] = [
    ['Picture', s.quality.hdr.join(' · ') || '—'],
    ['Video', s.quality.codec ?? '—'],
    ['Audio', s.quality.audio.join(' · ') || '—'],
    ['Size', size ? (s.sizeEstimated ? `~${size}` : size) : '—'],
    ['Addon', s.addonName],
    ['Starts', <StreamStatus key="starts" stream={s} provider={provider} />],
  ];
  return (
    <section
      aria-label="Stream details"
      className={cn('rounded-[var(--radius-card)] bg-card p-5', className)}
    >
      <p className={cn('font-display text-4xl font-bold tracking-tight', qualityTone(s))}>
        {qualityOf(s)}
      </p>
      {lastUsed && (
        <p className="mt-1 flex items-center gap-1 text-[13px] text-pink">
          <History className="h-3.5 w-3.5" aria-hidden /> Last used
        </p>
      )}
      <dl className="mt-4 space-y-2.5 text-[15px]">
        {facts.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-4">
            <dt className="shrink-0 text-muted">{label}</dt>
            <dd className="min-w-0 text-right font-medium break-words">{value}</dd>
          </div>
        ))}
        {s.hasSubtitles && (
          <div className="flex items-baseline justify-between gap-4">
            <dt className="shrink-0 text-muted">Subtitles</dt>
            <dd className="font-medium">Included</dd>
          </div>
        )}
      </dl>
      <p className="mt-4 border-t border-line pt-4 text-sm break-words text-muted">
        {s.filename ?? s.title}
      </p>
      {s.notWebReady && (
        <p className="mt-2 flex items-start gap-1.5 text-sm text-warning">
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          May not play in a browser
        </p>
      )}
      <Button data-stream-play onClick={onPlay} className="mt-4 w-full">
        <ActionIcon className={cn('h-4 w-4', !forDownload && 'fill-current')} aria-hidden />
        {forDownload ? 'Download' : 'Play'}
      </Button>
    </section>
  );
}

function StreamStatus({
  stream: s,
  provider,
}: {
  stream: NormalizedStream;
  provider?: ProviderId | null;
}) {
  if (s.cached === true)
    return (
      <span className="flex items-center gap-1 text-success">
        <Zap className="h-3.5 w-3.5" aria-hidden /> Instant
      </span>
    );
  if (s.cached === false) return <span className="text-pink-soft">Needs fetching</span>;
  if (s.availability === 'playable') return <span className="text-success">Ready</span>;
  if (s.availability === 'needs-resolution')
    return <span className="text-pink-soft">{provider ? PROVIDERS[provider].name : 'Debrid'}</span>;
  return null;
}
